import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BASE } from '@/api/client'
import { brandingApi } from '@/api/endpoints'
import bundledLogo from '@/assets/logo-horizontal.png'

/**
 * The logo the portal shows: the one uploaded in Settings > Company, or the
 * bundled artwork when none is uploaded. The API's URLs change with the file,
 * so a new upload is never hidden behind the browser's cache.
 *
 * Nothing is shown until the branding is known. Falling back to the bundled
 * artwork while the request was in flight put the old MetroMart logo on every
 * page load for a moment before the shop's own replaced it. So the last
 * branding this browser saw is remembered and shown at once — still checked
 * against the server on every load — and on a browser that has never seen it,
 * the logo's place stays empty for that moment instead. The bundled artwork
 * is used only when the server says nothing is uploaded, or cannot be reached.
 */
// As index.html has it, from public/.
const BUNDLED_FAVICON = '/favicon.png'

// The last branding seen, and the favicon URL public/branding-boot.js sets
// before the app loads. Per browser; a failure to read or write it (private
// mode, storage full) only means the old behaviour, never an error.
const CACHE_KEY = 'metromart-branding'
const FAVICON_KEY = 'metromart-favicon'

const remembered = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    return raw ? JSON.parse(raw) : undefined
  } catch {
    return undefined
  }
}

const remember = (data, favicon) => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(data))
    window.localStorage.setItem(FAVICON_KEY, favicon)
  } catch {
    // Not remembered: the next load waits for the server, as it always did.
  }
}

export function useBranding() {
  const { data, isError } = useQuery({
    queryKey: ['branding'],
    queryFn: brandingApi.get,
    // Start from what this browser saw last, dated long ago so it is checked
    // against the server straight away — a new upload elsewhere still shows.
    initialData: remembered,
    initialDataUpdatedAt: 0,
    // Not Infinity: with remembered data that would never be re-checked.
    staleTime: 5 * 60_000,
  })
  const absolute = (url) => (url ? `${BASE}${url}` : null)
  const favicon = absolute(data?.favicon) ?? BUNDLED_FAVICON

  // Kept in step with every change — a fetch, and an upload on the settings
  // screen, which writes the new branding straight into the query.
  useEffect(() => {
    if (data) remember(data, favicon)
  }, [data, favicon])

  // The browser tab's icon: the uploaded one, else the bundled favicon.png
  // index.html starts with. Every screen reads branding, so every screen —
  // the login page and the customer's order page too — sets it.
  useEffect(() => {
    if (!data) return
    let link = document.querySelector("link[rel='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    if (link.getAttribute('href') !== favicon) {
      link.type = 'image/png'
      link.href = favicon
    }
  }, [data, favicon])

  return {
    data,
    // null while the branding is not yet known: callers leave the space.
    siteLogo: data ? absolute(data.site_logo) ?? bundledLogo : isError ? bundledLogo : null,
    invoiceLogo: absolute(data?.invoice_logo),
    favicon,
  }
}

export default useBranding
